import { useState, useRef, useEffect, FC } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Row, Col, notification, Typography } from 'antd'
import {
  LoginForm,
  RequestNewPasswordForm,
  RegisterForm,
  ConfirmEmailForm,
} from '../components/'
import { useAuth } from '../context/AuthContext'
import { WELCOME_ROUTE_PATH } from '../routes'
import { getCurrentLangISO } from '../i18n'
import { useCustomisation } from '../hooks/useCustomisation'

type LoginRegisterFormsProps = {
  showRegisterForm: boolean
  redirectPath?: string
}

type Placement =
  | 'topLeft'
  | 'topRight'
  | 'bottomLeft'
  | 'bottomRight'
  | 'top'
  | 'bottom'

export const LoginRegisterForms: FC<LoginRegisterFormsProps> = ({
  showRegisterForm,
  redirectPath,
}: LoginRegisterFormsProps) => {
  const { Title } = Typography
  const { t } = useTranslation()
  const [showLoginForm, setShowLoginForm] = useState(true)
  const [showConfirmForm, setShowConfirmForm] = useState(false)
  const [email, setEmail] = useState('')
  const registerFormRef = useRef<HTMLDivElement>(null)
  const {
    token,
    user,
    onLogin,
    onRequestNewPassword,
    onRegister,
    emailVerificationNeeded,
    onConfirmEmail,
    onResendCode,
    onCheckStatus,
  } = useAuth()
  const { redirectUri } = useCustomisation()

  const navigate = useNavigate()
  const scrollToRef = () => {
    if (registerFormRef.current) {
      window.scrollTo(0, registerFormRef.current.offsetTop)
    }
  }

  useEffect(() => {
    if (token && user) {
      if (redirectUri) {
        window.location.replace(redirectUri)
      } else if (redirectPath) {
        navigate({ to: redirectPath })
      } else {
        navigate({ to: WELCOME_ROUTE_PATH })
      }
    }
  }, [token, user, redirectPath, navigate, redirectUri])

  const showError = (text, placement: Placement = 'bottomLeft') => {
    notification.error({
      message: t(text),
      placement,
    })
  }

  const showSuccess = (text, placement: Placement = 'bottomLeft') => {
    notification.success({
      message: t(text),
      placement,
    })
  }

  const handleSendSuccess = () => {
    setShowLoginForm(true)
    showSuccess('Login.RequestNewPassword.ok')
  }
  const handleConfirmSuccess = () => {
    setShowConfirmForm(false)
    showSuccess('Confirm.ok', 'bottomRight')
  }

  return (
    <Row className="login-register-forms" gutter={{ sm: 0, xl: 90 }}>
      {!showConfirmForm && (
        <Col xs={24} sm={14} xl={12}>
          <Title level={2} className="section-heading">
            {showRegisterForm && t('Login.HaveAnAccount')}
            <br />
            <strong>
              {showLoginForm ? t('Login.Login') : t('Login.RequestNewPassword')}
            </strong>
          </Title>
          {showRegisterForm && (
            <a
              onClick={scrollToRef}
              className="show-on-mobile small-link-text"
              data-testid="register-link"
            >
              <Title level={2}>
                {t('Login.NewUser')}
                <strong>{t('Login.CreateAccount')}</strong>
              </Title>
            </a>
          )}
          <br />
          {showLoginForm ? (
            <LoginForm
              onForgotPassword={() => setShowLoginForm(false)}
              onSubmit={(values) =>
                onLogin(
                  values,
                  () => {},
                  (error) =>
                    emailVerificationNeeded(error)
                      ? setShowConfirmForm(true)
                      : showError(error, 'bottomRight')
                )
              }
            />
          ) : (
            <RequestNewPasswordForm
              onBackToLogin={() => setShowLoginForm(true)}
              onSubmit={(values) =>
                onRequestNewPassword(values, handleSendSuccess, (error) =>
                  showError(error)
                )
              }
            />
          )}
        </Col>
      )}
      {showRegisterForm && (
        <Col
          xs={24}
          sm={14}
          xl={12}
          ref={registerFormRef}
          className="register-form"
        >
          {showConfirmForm ? (
            <ConfirmEmailForm
              email={email}
              onSubmit={(values) =>
                onConfirmEmail(values, handleConfirmSuccess, (error) =>
                  showError(error)
                )
              }
              onResend={() =>
                onResendCode(
                  { email: email },
                  () => {},
                  (error) =>
                    emailVerificationNeeded(error)
                      ? showSuccess('Confirm.Resend.ok')
                      : showError(error)
                )
              }
              onCheckStatus={() =>
                onCheckStatus(handleConfirmSuccess, () => {})
              }
              onBackToRegister={() => setShowConfirmForm(false)}
            />
          ) : (
            <RegisterForm
              onSubmit={(values) => {
                setEmail(values.email)
                onRegister(
                  {
                    language: getCurrentLangISO(),
                    ...values,
                  },
                  () => {},
                  (error) =>
                    emailVerificationNeeded(error)
                      ? setShowConfirmForm(true)
                      : showError(error, 'bottomRight')
                )
              }}
            />
          )}
        </Col>
      )}
    </Row>
  )
}
export default LoginRegisterForms
